import "katex/dist/katex.min.css";

import type { FC } from "react";
import { useEffect, useState } from "react";
import Latex from "react-latex-next";

import { TutoringFooter } from "./components/TutoringFooter";

import { ErrorModal, ViceMoznostiDoleOption } from "components";
import AnswerTutoring from "components/Answer/AnswerTutoring";
import { LastYearResultsBanner } from "components/LastYearResultsBanner/LastYearResultsBanner";
import { FirstIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/FirstIncorrectAnswer";
import { SecondIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/SecondIncorrectAnswer";

import type { CheckTutoringAnswerMutation, UserAssignmentDetailFragment } from "api/generated";
import { useCheckTutoringAnswerMutation } from "api/generated";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { processExerciseError } from "utils/processApiError";

import { AssignmentPrompt } from "components/AssignmentPrompt";
import { VideoAccordion } from "features/Tutoring/shared/VideoAccordion";
import { getAssignmentTime, resetAssignmentTimer, startAssignmentTimer, stopAssignmentTimer } from "store/slices/timer";
import { getTimeToLearn } from "utils/getTimeToLearn";
import { BooleanWithTypeGuard } from "utils/types/BooleanWithTypeGuard";
import { useTutoringContext } from "./context/TutoringContext";

interface Props {
  data: UserAssignmentDetailFragment;
  userAssignmentId: string;
  onNextClick: () => void;
}

const ViceMoznostiDole: FC<Props> = ({ data, userAssignmentId, onNextClick }) => {
  const questionsCount = data.assignment?.assignment?.questions.length ?? 0;

  const dispatch = useAppDispatch();
  const [answers, setAnswers] = useState<string[]>(new Array(questionsCount).fill(""));
  const [apiResults, setApiResults] = useState<boolean[]>([]);
  const time = useAppSelector(getAssignmentTime);

  const [sendAnswers, { error: apiMutationError, isLoading: isSendingAnswers }] = useCheckTutoringAnswerMutation();

  const [submit, setSubmit] = useState(false);
  const [isErrorModalVisible, setErrorModalVisible] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { videoTips, tutoringId, claimEvaluation } = useTutoringContext();
  const [activeAccordion, setAccordionActive] = useState<string | string[] | undefined | null>(null);

  useEffect(() => {
    dispatch(resetAssignmentTimer());
    dispatch(startAssignmentTimer());

    return () => {
      dispatch(resetAssignmentTimer());
    };
  }, []);

  useEffect(() => {
    if (apiMutationError === undefined) return;

    processExerciseError(apiMutationError, {
      assignmentId: userAssignmentId,
      selfEvaluation: null,
      userAnswers: answers ?? [],
      timeToSolve: time,
    });
    setErrorModalVisible(true);
  }, [apiMutationError]);

  useEffect(() => {
    if (!submit) return;

    sendAnswers({
      tutoringId,
      userAssignmentId,
      selfEvaluation: null,
      userAnswers: answers.map(answer => ({
        answers: [answer ?? ""],
      })),
      timeToSolve: time,
    })
      .then(response => {
        if ("data" in response && response.data) {
          setApiResults(response.data.tutoringAnswer.questionResults ?? []);
          processAnswerResponse(response);
          setErrorModalVisible(false);

          setAccordionActive(prev => {
            if (prev) return prev;
            const results = response.data.tutoringAnswer.questionResults ?? [];
            return results.length > 0 && !results.every(result => result) ? "0" : null;
          });
        }
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        dispatch(resetAssignmentTimer());
        dispatch(startAssignmentTimer());
        setSubmit(false);
      });
  }, [submit]);

  const processAnswerResponse = (response: { data: CheckTutoringAnswerMutation }) => {
    const { questionResults } = response.data.tutoringAnswer;

    if (!questionResults) return;

    const answersCopy = [...(answers ?? [])];
    questionResults.forEach((result, index) => {
      if (!result) answersCopy[index] = "";
    });

    setAnswers(answersCopy);
  };

  const onSubmit = () => {
    setSubmit(true);
    setAttempt(prev => prev + 1);
  };

  const handleNextClick = () => {
    dispatch(stopAssignmentTimer());
    onNextClick();
  };

  const handleChange = (index: number, value: string) => {
    const copy = [...answers];
    copy[index] = value;
    setAnswers(copy);
  };

  useEffect(() => {
    const areAllCorrect = apiResults.length > 0 && apiResults.every(result => result);
    if (areAllCorrect) {
      claimEvaluation({ tutoringId, userAssignmentId, userAnswers: apiResults }).catch(() => {});
    }
  }, [answers]);

  const areAnswersFilled = () => answers.length > 0 && answers.every(answer => answer.length > 0);

  const toggleAccordion = (state: boolean) => {
    setAccordionActive(state ? "0" : null);
  };

  const videoIds =
    data.assignment?.analysis?.hasSharedAnalysis === true
      ? [data.assignment.analysis.sharedAnalysis?.explainerVideo].filter(BooleanWithTypeGuard)
      : [
          ...new Set(
            data.assignment?.assignment?.questions
              .map(question => question.solutionAnalysis?.explainerVideo)
              .filter(BooleanWithTypeGuard),
          ),
        ];

  return (
    <div className="exercise__body">
      <div>
        {typeof data.assignment?.successRate === "number" && (
          <LastYearResultsBanner level={data.assignment?.successRate} />
        )}

        <AssignmentPrompt assignment={data.assignment?.assignment} />
        <VideoAccordion
          videoIds={videoIds}
          videoTips={videoTips}
          transcriptCs={data.assignment?.assignment?.transcriptCs}
          transcriptEn={data.assignment?.assignment?.transcriptEn}
          activeAccordion={activeAccordion}
          toggleAccordion={toggleAccordion}
        />
      </div>

      <div>
        {data.assignment?.assignment?.questions.map((question, qIndex) => (
          <div
            key={qIndex}
            className={`question ${apiResults.at(qIndex) === false ? "question--incorrect" : ""}`}
            {...(qIndex === 0 ? { id: "question" } : {})}
          >
            <Latex>{question.heading ?? ""}</Latex>

            {apiResults?.[qIndex] ? (
              <>
                <AnswerTutoring
                  subject={data.subject?.appName}
                  key={`answer-${qIndex}`}
                  index={qIndex}
                  assignment={data.assignment}
                  userAssignmentId={data.id}
                  type={apiResults?.[qIndex] ? "success" : "danger"}
                  timeToLearn={getTimeToLearn(data.timeToLearn)}
                >
                  {answers[qIndex]}
                </AnswerTutoring>
              </>
            ) : (
              <ViceMoznostiDoleOption
                index={qIndex}
                placeholder={question.placeholder ?? ""}
                options={question.options}
                isLast={qIndex + 1 === questionsCount}
                onChange={handleChange}
                selected={answers[qIndex]}
              />
            )}
          </div>
        ))}

        <ErrorModal
          onCancel={() => {
            setErrorModalVisible(false);
          }}
          onHide={() => {
            setErrorModalVisible(false);
          }}
          onRetry={() => {
            onSubmit();
          }}
          show={isErrorModalVisible}
        />

        {!isSendingAnswers && apiResults.includes(false) && (
          <>
            {!videoIds?.length || attempt === 1 ? (
              <FirstIncorrectAnswerModal
                incorrectCount={apiResults.filter(result => !result).length}
                questionsCount={questionsCount}
              />
            ) : null}

            {videoIds?.length && attempt > 1 ? (
              <SecondIncorrectAnswerModal videoIds={videoIds} attempt={attempt} />
            ) : null}
          </>
        )}

        <TutoringFooter
          onSubmit={onSubmit}
          isAnalysis={apiResults.length > 0 && apiResults.every(result => result)}
          goNext={handleNextClick}
          isSubmitDisabled={!areAnswersFilled() || isSendingAnswers}
        />
      </div>
    </div>
  );
};

export default ViceMoznostiDole;
